/**
 * Top-down preview of a lesson room, drawn from the same `planWorkspace` the XR
 * player builds the room with — so what an editor sees here is what a class
 * sees: every model's size and slot, the room each has to explode into, the
 * desk, and the lesson panel.
 */

import { useMemo } from 'react';
import * as THREE from 'three';

import { planWorkspace, type LessonLayout } from '../../../lib/three/lessonLayout';
import { THEME_LABELS, planLessonRoom, themeFor } from '../../../lib/three/roomTheme';
import type { WorkspaceAnchor } from '../../../lib/three/workspace';

export interface RoomContext {
  subject?: string;
  classNumber?: number | null;
  chapterId?: string;
  topicId?: string;
  /** 0-based position of the topic in its chapter. */
  topicIndex?: number;
}

interface Props {
  layout: LessonLayout;
  assets: { id: string; name: string }[];
  room?: RoomContext;
}

/** A seated viewer at the origin, facing -Z (up the page). */
const ANCHOR: WorkspaceAnchor = {
  position: new THREE.Vector3(0, 1.6, 0),
  forward: new THREE.Vector3(0, 0, -1),
  eyeHeight: 1.6,
  groundY: 0,
};

/** Metres to SVG units, and the visible window: wide enough for a panel turned to ±70°. */
const S = 100;
const VIEW = { x: -2.1 * S, y: -2.4 * S, w: 4.2 * S, h: 2.65 * S };

export function LayoutPreview({ layout, assets, room }: Props) {
  const frame = useMemo(
    () => planWorkspace(ANCHOR, layout, assets.map((a) => a.id)),
    [layout, assets]
  );
  // The same themed room the player builds.
  const roomPlan = useMemo(() => {
    const theme = themeFor({
      subject: room?.subject,
      classNumber: room?.classNumber,
      preset: layout.preset,
      override: layout.roomTheme === 'auto' ? null : layout.roomTheme,
    });
    if (!layout.showProps) return { theme, props: [] };
    return planLessonRoom(frame, theme, room ?? {}, layout.propSeed, {
      bearingDeg: frame.panelBearingDeg,
      widthDeg: frame.panelWidthDeg,
    });
  }, [frame, layout, room]);
  const nameOf = (id: string) => assets.find((a) => a.id === id)?.name ?? id;

  const px = (v: THREE.Vector3) => ({ x: v.x * S, y: v.z * S });
  const panel = px(frame.panel.position);
  // The panel faces the viewer; draw it as its top edge, across the line of sight.
  const across = new THREE.Vector3(Math.cos(frame.panel.yaw), 0, -Math.sin(frame.panel.yaw));
  const half = (frame.panel.width / 2) * S;
  const dock = px(frame.dock.center);

  return (
    <figure className="rounded-lg border border-border bg-muted/30 p-3">
      <svg
        viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
        className="w-full h-auto max-h-[340px]"
        role="img"
        aria-label={`Top-down view: ${frame.slots.length} models, panel ${frame.panelBearingDeg < -1 ? 'left' : frame.panelBearingDeg > 1 ? 'right' : 'ahead'}`}
      >
        {/* Comfortable viewing cone, ±45° */}
        <path
          d={`M0 0 L${-Math.sin(Math.PI / 4) * 2.4 * S} ${-Math.cos(Math.PI / 4) * 2.4 * S} M0 0 L${Math.sin(Math.PI / 4) * 2.4 * S} ${-Math.cos(Math.PI / 4) * 2.4 * S}`}
          className="stroke-muted-foreground/30"
          strokeDasharray="4 4"
          fill="none"
        />
        {/* Themed pieces around the desk */}
        {roomPlan.props.map((p, i) => (
          <g key={`prop-${i}`}>
            <circle cx={p.x * S} cy={p.z * S} r={p.radius * S} className="fill-stone-500/40 stroke-stone-400/70" />
            <text x={p.x * S} y={p.z * S + 3} textAnchor="middle" className="fill-stone-200 text-[8px]">
              {p.type === 'table' ? 'table' : p.kind}
            </text>
          </g>
        ))}
        {/* Desk */}
        <rect
          x={dock.x - (frame.dock.width / 2) * S}
          y={dock.y - (frame.dock.depth / 2) * S}
          width={frame.dock.width * S}
          height={frame.dock.depth * S}
          rx={4}
          className="fill-sky-900/40 stroke-sky-400/70"
        />
        {/* Models: resting footprint, and the dashed room each may explode into */}
        {frame.slots.map((slot) => {
          const p = px(slot.position);
          const r = slot.targetSize * Math.SQRT1_2 * S;
          const room = r + slot.explodeReach * slot.targetSize * S;
          return (
            <g key={slot.assetId}>
              <circle cx={p.x} cy={p.y} r={room} className="fill-none stroke-emerald-400/40" strokeDasharray="3 3" />
              <circle
                cx={p.x}
                cy={p.y}
                r={r}
                className={slot.primary ? 'fill-amber-400/70 stroke-amber-300' : 'fill-emerald-500/60 stroke-emerald-300'}
              >
                <title>{`${nameOf(slot.assetId)} — ${(slot.targetSize * 100).toFixed(0)} cm${slot.primary ? ' (main)' : ''}`}</title>
              </circle>
            </g>
          );
        })}
        {/* Lesson panel */}
        <line
          x1={panel.x - across.x * half}
          y1={panel.y - across.z * half}
          x2={panel.x + across.x * half}
          y2={panel.y + across.z * half}
          className="stroke-violet-400"
          strokeWidth={6}
          strokeLinecap="round"
        />
        <text x={panel.x} y={panel.y - 10} textAnchor="middle" className="fill-violet-300 text-[10px]">
          Lesson panel
        </text>
        {/* Viewer */}
        <circle cx={0} cy={0} r={7} className="fill-foreground" />
        <text x={0} y={-12} textAnchor="middle" className="fill-muted-foreground text-[9px]">
          Student
        </text>
      </svg>
      <figcaption className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400 mr-1" />Main model</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1" />Supporting</span>
        <span>Dashed: room to explode</span>
        {frame.rows === 2 && <span>Two rows (crowded desk)</span>}
        <span>Room: {THEME_LABELS[roomPlan.theme.id].split(' — ')[0]}{roomPlan.theme.band === 'young' ? ' (classes 1–5)' : ''}</span>
        {layout.realScale && (
          <span className={frame.notToScale ? 'text-amber-400' : 'text-teal-400'}>
            {frame.notToScale ? 'Sizes not to scale (small models enlarged to stay readable)' : 'Shown to true scale'}
          </span>
        )}
      </figcaption>
    </figure>
  );
}
